import React, { Component } from 'react';
import { connect } from 'react-redux';

import { Drawer, Collapse, Divider } from 'antd';
import _ from 'lodash';

import scribe from '@grab-x/scribe-web-sdk';
import ScribeEventName from 'omega-rtc/constants/scribe';
import FAQFeedbackForm from 'omega-rtc/views/ucd/trident/common/FAQFeedbackForm';

import { FAQ_PERFORMANCE_LIST } from 'omega-rtc/constants/metrics';

import type { StoreState } from 'reducers';
import type { UserState } from 'reducers/user';

import './FAQDrawer.scss';


type Props = {
  onClose: Function,
  visible: boolean,
  user: UserState,
};

const { Panel } = Collapse;
const LikeIcon = ({ color, onClick }) => (
  <svg
    className={'like-icon'}
    onClick={onClick}
    width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill={color}
      fillRule="evenodd" clipRule="evenodd" d="M2.625 6.61786C2.625 5.90655 2.928 5.22892 3.45813 4.75466L7.89519 0.785147C8.28296 0.438233 8.85803 0.402472 9.2858 0.69867C9.72196 1.00068 9.88788 1.56634 9.68395 2.0561L8.25 5.5L11.3752 5.5C12.9322 5.5 14.1105 6.90791 13.8361 8.44055L13.1647 12.1906C12.9514 13.3824 11.9146 14.25 10.7039 14.25L5.125 14.25C3.74429 14.25 2.625 13.1307 2.625 11.75V6.61786ZM0.125 13V6.125C0.125 5.77983 0.404822 5.5 0.75 5.5C1.09518 5.5 1.375 5.77983 1.375 6.125V13C1.375 13.3452 1.09518 13.625 0.75 13.625C0.404822 13.625 0.125 13.3452 0.125 13Z"
    />
  </svg>
);
const DislikeIcon = ({ color, onClick }) => (
  <svg
    className={'dislike-icon'}
    onClick={onClick}
    width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill={color}
      fillRule="evenodd" clipRule="evenodd" d="M11.375 8.00717C11.375 8.71848 11.072 9.39611 10.5419 9.87037L6.10481 13.8399C5.71704 14.1868 5.14197 14.2226 4.7142 13.9264C4.27804 13.6244 4.11212 13.0587 4.31605 12.5689L5.75 9.12503L2.6248 9.12503C1.06779 9.12503 -0.110453 7.71712 0.163925 6.18448L0.835258 2.43448C1.04862 1.24266 2.08537 0.375032 3.29614 0.375032L8.875 0.375033C10.2557 0.375033 11.375 1.49432 11.375 2.87503L11.375 8.00717ZM13.875 1.62502L13.875 8.50002C13.875 8.8452 13.5952 9.12502 13.25 9.12502C12.9048 9.12502 12.625 8.8452 12.625 8.50002L12.625 1.62502C12.625 1.27984 12.9048 1.00002 13.25 1.00002C13.5952 1.00002 13.875 1.27984 13.875 1.62502Z"
    />
  </svg>
);

const LIKE_REACTION = 'like';
const DISLIKE_REACTION = 'dislike';

class FAQDrawer extends Component {
  props: Props;

  constructor(props) {
    super(props);
    this.state = {
      likedFAQs: {},
      showFeedbackMessages: {},
      showFAQFeedbackModal: false,
      selectedFaqItem: {},
    };
    this.timeoutIds = {}; // Store timeout IDs
  }

  componentDidMount() {
    const storedLikedFAQs = JSON.parse(localStorage.getItem('likedFAQs')) || {};
    this.setState({ likedFAQs: storedLikedFAQs });
  }

  componentWillUnmount() {
    // Clear all the timeouts when the component is unmounted
    Object.values(this.timeoutIds).forEach((timeoutId) => clearTimeout(timeoutId));
  }

  handleReactionClick = (faq, reaction) => {
    const { faqID, title } = faq;
    const { likedFAQs } = this.state;
    const updatedLikedFAQs = { ...likedFAQs, [faqID]: reaction };
    this.setState({ likedFAQs: updatedLikedFAQs });

    if (reaction === LIKE_REACTION) {
      // Clear the existing timeout for the FAQ.
      clearTimeout(this.timeoutIds[faqID]);

      // Set a new timeout to show the feedback message after 0.8 seconds.
      this.timeoutIds[faqID] = setTimeout(() => {
        this.setState({ showFeedbackMessages: { ...this.state.showFeedbackMessages, [faqID]: true } });
      }, 800);
    } else {
      this.openFAQFeedbackModal(faq);
    }
    // TODO: if dislike should show the feedback form.

    // send scribe event.
    scribe.sendEvent(ScribeEventName.CAMPAIGN_METRICS, {
      type: reaction,
      title,
      email: _.get(this.props, 'user.email'),
    });


    localStorage.setItem('likedFAQs', JSON.stringify(updatedLikedFAQs));
  };

  getReaction = (faqID) => {
    const { likedFAQs } = this.state;
    return likedFAQs[faqID] || null;
  };

  openFAQFeedbackModal = (faq) => {
    this.setState({
      showFAQFeedbackModal: true,
      selectedFaqItem: faq,
    });
  }

  closeFAQFeedbackModal = (e) => {
    e.preventDefault();
    this.setState({
      showFAQFeedbackModal: false,
      selectedFaqItem: {},
    });
  }

  render() {
    const { visible, onClose } = this.props;
    const { showFeedbackMessages } = this.state;

    return (
      <Drawer
        className={'faq-drawer-wrapper'}
        placement="right"
        title="Benchmark Methodology"
        width={500}
        visible={visible}
        onClose={onClose}
        style={{ maxHeight: '100%', overflowY: 'auto' }}
      >
        {_.map(FAQ_PERFORMANCE_LIST, (faq) => {
          const { faqID, title, content } = faq;

          return (
            <Collapse expandIconPosition="right" key={faqID}>
              <Panel header={title} key={faqID}>
                <p className="main-content">{content}</p>
                <Divider />
                <div className="faq-footer">
                  {!showFeedbackMessages[faqID] &&
                    <p className="reaction-corner">
                      Is the explanation helpful?
                      <LikeIcon
                        color={this.getReaction(faqID) === LIKE_REACTION ? '#00B14F' : '#575757'}
                        onClick={() => this.handleReactionClick(faq, LIKE_REACTION)}
                      />
                      <DislikeIcon
                        color={this.getReaction(faqID) === DISLIKE_REACTION ? '#FF3B30' : '#575757'}
                        onClick={() => this.handleReactionClick(faq, DISLIKE_REACTION)}
                      />
                    </p>
                  }
                  <p
                    className="feedback-message"
                    style={{
                      transform: showFeedbackMessages[faqID] ? 'translateX(0)' : 'translateX(100%)',
                      fontSize: showFeedbackMessages[faqID] ? 'inherit' : '0',
                    }}
                  >Thank you for your feedback!</p>
                </div>
              </Panel>
            </Collapse>
          );
        })}
        {this.state.showFAQFeedbackModal &&
          <FAQFeedbackForm
            closeModal={this.closeFAQFeedbackModal}
            faqInstance={this.state.selectedFaqItem}
          />
        }
      </Drawer>
    );
  }
}

function mapStateToProps(state: StoreState) {
  return {
    user: state.app.user,
  };
}

export default connect(mapStateToProps, {
})(FAQDrawer);

